import { useState, FormEvent } from 'react'
import { useAuth } from '../../context/AuthContext'
import { setTrustOverride, clearTrustOverride } from './trustApi'
import { MAX_TRUST_SCORE } from './types'

interface Props {
  vendorUserId:   number
  currentScore:   number
  computedScore:  number
  isOverridden:   boolean
  overrideScore:  number | null
  onSave:         () => void
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase().slice(0, 4)
  }
  const compact = name.replace(/\s+/g, '').toUpperCase()
  return compact.length >= 2 ? compact.slice(0, 4) : 'XX'
}

export default function TrustScoreOverridePanel({
  vendorUserId,
  currentScore,
  computedScore,
  isOverridden,
  overrideScore,
  onSave,
}: Props) {
  const { user } = useAuth()
  const contractorName = user?.name ?? user?.company_name ?? 'Contractor'

  const [scoreVal, setScoreVal] = useState(String(overrideScore ?? computedScore))
  const [reason, setReason]     = useState('')
  const [error, setError]       = useState<string | null>(null)
  const [saving, setSaving]     = useState(false)
  const [clearing, setClearing] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const score = parseInt(scoreVal, 10)
    if (Number.isNaN(score) || score < 0 || score > MAX_TRUST_SCORE) {
      setError(`Score must be 0–${MAX_TRUST_SCORE}`)
      return
    }
    if (reason.trim().length < 10) {
      setError('Reason must be at least 10 characters')
      return
    }
    setSaving(true)
    try {
      await setTrustOverride(vendorUserId, {
        score,
        reason: reason.trim(),
        initials: initialsFromName(contractorName),
      })
      setReason('')
      onSave()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save override')
    } finally {
      setSaving(false)
    }
  }

  async function handleClear() {
    setError(null)
    setClearing(true)
    try {
      await clearTrustOverride(vendorUserId)
      onSave()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear override')
    } finally {
      setClearing(false)
    }
  }

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        border: '1px solid var(--line)',
        borderRadius: 'var(--rs)',
        background: 'var(--bg)',
      }}
    >
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>
            Score (0–{MAX_TRUST_SCORE})
          </span>
          <input
            type="number"
            min={0}
            max={MAX_TRUST_SCORE}
            value={scoreVal}
            onChange={e => setScoreVal(e.target.value)}
            className="fld-i"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13 }}
            required
          />
          <span style={{ fontSize: 10, color: 'var(--ink4)' }}>
            Soren: {computedScore} · Effective: {currentScore}
          </span>
        </label>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>
            Reason (private — not shown to vendor)
          </span>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            className="fld-i"
            style={{ width: '100%', padding: '8px 10px', fontSize: 12, resize: 'vertical' }}
            required
          />
        </label>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>
            Overridden by
          </span>
          <input
            type="text"
            readOnly
            value={contractorName}
            className="fld-i"
            style={{ width: '100%', padding: '8px 10px', fontSize: 12, background: 'var(--surface)', color: 'var(--ink4)' }}
          />
        </label>

        {error && (
          <p style={{ fontSize: 11, color: 'var(--red)', margin: '0 0 10px' }} role="alert">{error}</p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="submit" className="btn b-dk btn-sm" disabled={saving}>
            {saving ? 'Saving…' : isOverridden ? 'Save override' : 'Save override'}
          </button>
          {isOverridden && (
            <button
              type="button"
              className="btn b-ol btn-sm"
              disabled={clearing}
              onClick={() => { void handleClear() }}
            >
              {clearing ? 'Clearing…' : 'Clear override'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
